import * as React from "react";

import { datadogRum } from "@datadog/browser-rum";
import { useFormContext } from "react-hook-form";

import { Button } from "@ps-design-system/components/Button";
import { FormField } from "@ps-design-system/components/Form";
import { Text } from "@ps-design-system/components/Text";

import { useIntl } from "react-intl-compat";

import type { ListingFsvFormValues } from "../../ListingFsvForm.schema";

import { getMenuPdfPreviewUrl, openMenuUploadWidget } from "./MenuField.utils";

/**
 * The FSV menu: shows the current menu (image, or a first-page preview and a
 * link for PDFs) and lets the host upload a replacement to Cloudinary. The
 * uploaded URL is held in the form; saving it to the listing happens on save.
 */
const MenuField = () => {
  const intl = useIntl();
  const { control } = useFormContext<ListingFsvFormValues>();
  const [isUploading, setIsUploading] = React.useState(false);
  const [errorMsg, setErrorMsg] = React.useState<string>();
  // The PDF preview URL that failed to load, so it falls back to the link
  const [failedPreviewUrl, setFailedPreviewUrl] = React.useState<string>();

  return (
    <FormField
      control={control}
      name="menuUrl"
      render={({ field }) => {
        const handleUpload = async () => {
          setErrorMsg(undefined);
          setIsUploading(true);
          try {
            const uploaded = await openMenuUploadWidget(intl);
            if (uploaded) {
              field.onChange(uploaded.secure_url);
            }
          } catch (error) {
            datadogRum.addError(error, { context: "FSV menu upload" });
            setErrorMsg(
              intl.formatMessage({
                id: "hosting.listing_fsv.menu_upload_error",
                defaultMessage:
                  "We couldn't upload your menu. Please try again.",
              }),
            );
          } finally {
            setIsUploading(false);
          }
        };

        const isPdf = field.value?.toLowerCase().endsWith(".pdf");
        const pdfPreviewUrl =
          isPdf && field.value ? getMenuPdfPreviewUrl(field.value) : undefined;
        const menuAlt = intl.formatMessage({
          id: "hosting.listing_fsv.menu_image_alt",
          defaultMessage: "Menu",
        });

        return (
          <div className="tw-mb-16" data-testid="fsv-form-menu-field">
            <Text typeface="body-2-m" element="p">
              {intl.formatMessage({
                id: "hosting.listing_fsv.menu_label",
                defaultMessage: "Menu",
              })}
            </Text>

            {field.value &&
              (isPdf ? (
                <>
                  {pdfPreviewUrl && pdfPreviewUrl !== failedPreviewUrl && (
                    <a
                      className="tw-mt-8 tw-block"
                      href={field.value}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <img
                        className="tw-max-h-[320px] tw-max-w-full tw-rounded-4 tw-border tw-border-gray-300"
                        src={pdfPreviewUrl}
                        alt={menuAlt}
                        onError={() => setFailedPreviewUrl(pdfPreviewUrl)}
                        data-testid="fsv-form-menu-pdf-preview"
                      />
                    </a>
                  )}
                  <a
                    className="tw-mt-8 tw-block tw-underline"
                    href={field.value}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="fsv-form-menu-pdf-link"
                  >
                    {intl.formatMessage({
                      id: "hosting.listing_fsv.menu_view_pdf",
                      defaultMessage: "View menu (PDF)",
                    })}
                  </a>
                </>
              ) : (
                <img
                  className="tw-mt-8 tw-max-h-[320px] tw-max-w-full tw-rounded-4"
                  src={field.value}
                  alt={menuAlt}
                  data-testid="fsv-form-menu-image"
                />
              ))}

            <div className="tw-mt-16">
              <Button
                variant="outline-2"
                onClick={handleUpload}
                showSpinner={isUploading}
                disabled={isUploading}
                testId="fsv-form-menu-upload-button"
              >
                {field.value
                  ? intl.formatMessage({
                      id: "hosting.listing_fsv.menu_replace_button",
                      defaultMessage: "Replace menu",
                    })
                  : intl.formatMessage({
                      id: "hosting.listing_fsv.menu_upload_button",
                      defaultMessage: "Upload menu",
                    })}
              </Button>
            </div>

            {errorMsg && (
              <Text
                typeface="body-1"
                element="p"
                className="tw-mt-8 tw-text-red-800"
                testId="fsv-form-menu-upload-error"
              >
                {errorMsg}
              </Text>
            )}
          </div>
        );
      }}
    />
  );
};

export { MenuField };
